<template>
  <div class="projekte-page">
    <Teleport to="#site-header-slot" v-if="windowWidth">
      <ProjectOverviewToggle
        class="toggle"
        @toggle="toggleView"
        :current-view="view"
        v-show="windowWidth >= 800"
      />
    </Teleport>
    <main class="main">
      <div class="inner" :class="[`-${view}`]">
        <ProjectOverviewToggle
          class="toggle"
          @toggle="toggleView"
          :current-view="view"
          v-if="windowWidth <= 799"
        />
        <nav
          ref="projectFilterElement"
          class="filters project-filter"
          :class="{ '-open': isFilersPanelOpen }"
          aria-label="Filter"
        >
          <button
            type="button"
            @click="toggleFiltersPanel()"
            class="toggle button-toggle"
            :class="{ '-open': isFilersPanelOpen }"
          >
            {{ general?.projectOpenCloseFilter }}
          </button>
          <p class="panel-title" @click="toggleFiltersPanel()">Filter</p>
          <ul class="filter-list">
            <li class="filter">
              <button
                type="button"
                :class="{ '-active': !isKindActive('service') }"
                @click="clearKind('service')"
                class="button button-simple"
              >
                {{ general?.projectAllServicesHeading }}
              </button>
            </li>
            <li class="filter" v-for="s in allServices" :key="s.id">
              <button
                type="button"
                class="button button-simple"
                :class="{ '-active': isFilter('service', s.slug) }"
                @click="s.slug && setFilter('service', s.slug)"
              >
                {{ s.title }}
              </button>
            </li>
          </ul>
          <ul v-if="allWorks.length" class="filter-list">
            <li class="filter">
              <button
                type="button"
                class="button button-simple"
                :class="{ '-active': !isKindActive('work') }"
                @click="clearKind('work')"
              >
                {{ general?.projectAllWorkHeading }}
              </button>
            </li>
            <li class="filter" v-for="work in allWorks" :key="work.id">
              <button
                type="button"
                class="button button-simple"
                :class="{ '-active': isFilter('work', work.slug) }"
                @click="work.slug && setFilter('work', work.slug)"
              >
                {{ work.title }}
              </button>
            </li>
          </ul>
          <ul v-if="allEvents.length" class="filter-list">
            <li class="filter">
              <button
                type="button"
                class="button button-simple"
                :class="{ '-active': !isKindActive('event') }"
                @click="clearKind('event')"
              >
                {{ general?.projectAllEventsHeading }}
              </button>
            </li>
            <li class="filter" v-for="eventItem in allEvents" :key="eventItem.id">
              <button
                type="button"
                class="button button-simple"
                :class="{ '-active': isFilter('event', eventItem.slug) }"
                @click="eventItem.slug && setFilter('event', eventItem.slug)"
              >
                {{ eventItem.title }}
              </button>
            </li>
          </ul>
        </nav>
        <div class="projectfocus">
          <ProjectTeaserImage
            :key="activeProject.id"
            :data-old="oldActiveProject ?? undefined"
            :data="activeProject"
            v-if="activeProject"
          />
        </div>
        <div class="projects" v-if="view === 'raster'">
          <ProjectTeaser :project="p" v-for="p in filteredProjects" :key="p.id" />
        </div>
        <div class="projects" v-else-if="view === 'list'">
          <ProjectTeaserTitle
            :data="p"
            v-for="p in filteredProjects"
            :key="p.id"
            @mouseenter="setActiveProject(p)"
            :class="{ '-active': activeProject === p }"
          />
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import ProjectTeaser from '~/components/content-modules/components/ProjectTeaser.vue';
import ProjectTeaserTitle from '~/components/content-modules/components/ProjectTeaserTitle.vue';
import ProjectTeaserImage from '~/components/content-modules/components/ProjectTeaserImage.vue';
import ProjectOverviewToggle from '~/components/content-modules/components/ProjectOverviewToggle.vue';
import type { GetAllProjectsQuery } from '#gql';
import type { GetAllServicesQuery } from '#gql';
import type { GetAllProjectTaxonomiesQuery } from '#gql';
import type { ProjectFragment } from '#gql';
import { type SeoMetaTagType, toHead } from 'vue-datocms';
import type { GetPageBySlugQuery } from '#gql';
import type { GeneralFragment } from '#gql';
import type { ComputedRef } from 'vue';

enum ProjectView {
  List = 'list',
  Raster = 'raster'
}

type FilterKind = 'service' | 'work' | 'event';

const general = inject<ComputedRef<GeneralFragment | null>>('general');

const route = useRoute();

const { data: pageData, error: pageError } = await useAsyncGql({
  operation: 'GetPageBySlug',
  variables: { slug: String(route.name) }
});

const { data: projectsData, error: projectsError } = await useAsyncGql({
  operation: 'GetAllProjects'
});

const { data: servicesData, error: servicesError } = await useAsyncGql({
  operation: 'GetAllServices'
});

const { data: taxonomiesData } = await useAsyncGql({
  operation: 'GetAllProjectTaxonomies'
});

const page = (pageData.value as GetPageBySlugQuery).page;
const allProjects = (projectsData.value as GetAllProjectsQuery).allProjects;
const allServices = (servicesData.value as GetAllServicesQuery).allServices;
const allWorks = (taxonomiesData.value as GetAllProjectTaxonomiesQuery).allWorks ?? [];
const allEvents = (taxonomiesData.value as GetAllProjectTaxonomiesQuery).allEvents ?? [];

const projectFilterElement = ref<HTMLElement | null>(null);
const isFilersPanelOpen = ref<boolean>(false);
const activeFilters = ref<Record<FilterKind, string | null>>({
  service: null,
  work: null,
  event: null
});
const oldActiveProject = ref<null | ProjectFragment>(null);
const activeProject = ref<null | ProjectFragment>(null);
const view = ref<ProjectView>(ProjectView.Raster);
const { width: windowWidth } = useWindowSize();

onClickOutside(projectFilterElement, function () {
  toggleFiltersPanel(false);
});

const filteredProjects = computed(function () {
  const { service, work, event } = activeFilters.value;

  return allProjects.filter(function (project) {
    if (service && project.service?.slug !== service) {
      return false;
    }

    if (work && !project.work?.some((item) => item.slug === work)) {
      return false;
    }

    if (event && !project.event?.some((item) => item.slug === event)) {
      return false;
    }

    return true;
  });
});

watch(
  () => filteredProjects.value.length,
  function () {
    if (filteredProjects.value[0]) {
      setActiveProject(filteredProjects.value[0]);
    }
  }
);

setActiveProject(filteredProjects.value?.[0] ?? null);

function setActiveProject(p: null | ProjectFragment) {
  oldActiveProject.value = activeProject.value;
  activeProject.value = p;
}

function isFilter(kind: FilterKind, slug: string | null | undefined) {
  return Boolean(slug) && activeFilters.value[kind] === slug;
}

function isKindActive(kind: FilterKind) {
  return activeFilters.value[kind] !== null;
}

function clearKind(kind: FilterKind) {
  activeFilters.value[kind] = null;
  setActiveProject(null);
}

function setFilter(kind: FilterKind, slug: string) {
  activeFilters.value[kind] = slug;
  setActiveProject(null);
}

function toggleView(v: ProjectView) {
  view.value = v;
}

function toggleFiltersPanel(open = !isFilersPanelOpen.value) {
  isFilersPanelOpen.value = open;
}

useHead(toHead(page?.seo as SeoMetaTagType[]));

definePageMeta({
  pageTransition: {
    name: 'from-index',
    mode: 'out-in',
    onLeave: async function (el, done) {
      await useSleep(100);

      const tl = gsap.timeline({
        onComplete: function () {
          done();
        }
      });

      tl.to(el, {
        opacity: 0
      });
    }
  }
});
</script>

<style scoped lang="scss">
.projekte-page {
  > .main {
    > .inner {
      display: grid;

      @media (--vs) {
        grid-template-columns: 1fr;
        gap: 2rem;
        padding: var(--base-component-padding-block) var(--base-component-padding-inline) 5rem
          var(--base-component-padding-inline);
      }

      @media (--vl) {
        grid-template-columns: 1fr 2fr;
        grid-template-rows: auto 1fr;
        gap: 2.5rem;
        padding: 5rem var(--base-component-padding-inline) 0 var(--base-component-padding-inline);
      }

      > .toggle {
        @media (--vl) {
          display: none;
        }
      }

      &.-raster {
        padding-bottom: 5rem;

        > .filters {
          @media (--vl) {
            padding-top: 3.5rem;
            grid-area: 1 / 1 / 2 / 3;
          }
        }

        > .projectfocus {
          display: none;
        }

        > .projects {
          display: grid;

          @media (--vs) {
            gap: 1.5rem;
            grid-template-columns: repeat(1, minmax(0, 1fr));
            padding: 0 0 2rem 0;

            :deep(img),
            :deep(figure) {
              --scale: 1;
              scale: 1 !important;
            }
          }

          @media (--vl) {
            gap: 2.5rem 2rem;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            padding-top: 6rem;
            grid-area: 2 / 1 / 3 / 3;
          }
        }
      }

      &.-list {
        @media (--vs) {
          display: block;
        }

        @media (--vl) {
          height: 100vh;
          gap: 3rem 15vw;
        }

        > .toggle {
        }

        > .filters {
          @media (--vl) {
            display: none;
          }
        }

        > .projectfocus {
          @media (--vs) {
            display: none;
          }
          @media (--vl) {
            grid-area: 1 / 1 / 3 / 2;
            padding-bottom: var(--base-component-padding-inline);
            align-self: flex-end;
          }
        }

        > .projects {
          @include hide-scrollbar();

          @media (--vs) {
            padding-bottom: 2rem;
          }

          @media (--vl) {
            grid-area: 1 / 2 / 3 / 3;
            padding-top: 3.5rem;
            padding-bottom: 3.5rem;
            overflow-y: auto;
          }
        }
      }
    }
  }
}

.project-filter {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (--vl) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2.5rem 2rem;
  }

  @media (--vs) {
    position: fixed;
    inset: auto 0 0 0;
    width: 100%;
    z-index: 10;
    max-height: calc(100dvh - 5rem);
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1.5rem;
    background-color: var(--color-black);
    transition: transform 300ms;
    transform: translateY(calc(100% - 4.5rem));
    gap: 2.5rem;

    > .filter-list,
    > .toggle {
      transition: opacity 300ms;
    }

    > .toggle {
      filter: invert(1);
    }

    > .filter-list {
      opacity: 0;
    }

    &.-open {
      transform: translateY(0);

      > .filter-list {
        opacity: 1;
      }
    }
  }

  > .toggle {
    @media (--vl) {
      display: none;
    }

    @media (--vs) {
      top: 1.5rem;
      right: 1.5rem;
      z-index: 10;
    }
  }

  > .panel-title {
    @include text-xl(700);

    color: var(--color-white);
    line-height: 1 !important;
    margin: 0;

    @media (--vs) {
      position: sticky;
      top: 0;
      z-index: 1;
      padding-bottom: 0.5rem;
      background-color: var(--color-black);
    }

    @media (--vl) {
      display: none;
    }
  }

  .filter {
    &:not(:last-of-type) {
      @media (--vs) {
        padding: 0 0 1.25rem 0;
      }
    }

    > .button {
      @media (--vs) {
        @include text-xl(700);

        line-height: 1 !important;
        width: 100%;
      }
    }

    > .button:not(.-active),
    > .button:not(.-active):hover {
      @media (--vs) {
        color: var(--color-grey);
      }
    }

    > .button.-active,
    > .button.-active:hover {
      @media (--vs) {
        color: var(--color-white);
      }
    }
  }
}
</style>
